import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Sparkles, Gift, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';
// import { toast } from 'sonner'; // Temporarily disabled due to React child error
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreeRegistrationPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.username || formData.username.length < 2) {
      newErrors.username = 'Nom d\'utilisateur requis (min. 2 caractères)';
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Mot de passe requis (min. 6 caractères)';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // toast.error('Veuillez corriger les erreurs du formulaire');
      alert('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/auth/register-free`, {
        email: formData.email,
        username: formData.username,
        password: formData.password
      });

      const { access_token, user } = response.data;

      // Store token and user data
      localStorage.setItem('tkale_token', access_token);
      localStorage.setItem('tkale_user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // toast.success(`Bienvenue ${user.username} ! Vous avez reçu ${user.credit_balance} crédits de bienvenue.`);
      alert(`Bienvenue ${user.username} ! Vous avez reçu ${user.credit_balance} crédits de bienvenue.`);
      
      // Redirect to dashboard immediately
      navigate('/dashboard');

    } catch (error) {
      alert(typeof message === 'string' ? message : 'Erreur lors de l inscription');
      // toast.error(message);
      alert(message);
      
      if (message.includes('déjà enregistré')) {
        setErrors({ email: 'Cet email est déjà utilisé' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const features = [
    'Accès à toutes les bulles en preview',
    '10 crédits T-Kalé offerts',
    'Chatbot acoustique disponible',
    'Découverte de la plateforme',
    'Possibilité d\'upgrade à tout moment'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-purple-900 to-teal-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-cyan-300 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0
            }}
            animate={{
              opacity: [0, 0.6, 0],
              scale: [0.5, 1.5, 0.5]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      {/* Back button - ramène au portail Client/Visiteur */}
      <Button
        variant="ghost"
        className="absolute top-4 left-4 text-cyan-300 hover:text-cyan-100 z-10"
        onClick={() => navigate('/', { state: { fromLogin: true } })}
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Retour
      </Button>

      <div className="relative z-10 w-full max-w-5xl grid md:grid-cols-2 gap-8">
        {/* Left side - Features */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex flex-col justify-center text-white space-y-6"
        >
          <div>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="text-8xl mb-4"
            >
              🎁
            </motion.div>
            <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Découverte Gratuite
            </h1>
            <p className="text-xl text-cyan-200 mb-8">
              Commencez votre aventure T-Kalé sans engagement
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-cyan-300 mb-4">
              Ce qui est inclus :
            </h3>
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-cyan-300" />
                </div>
                <span className="text-lg text-cyan-100">{feature}</span>
              </motion.div>
            ))}
          </div>

          <Alert className="bg-yellow-500/20 border-yellow-500/50 mt-6">
            <AlertDescription className="text-yellow-100">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-yellow-400" />
                <div>
                  <span className="font-semibold">Bonus de bienvenue :</span>{' '}
                  Recevez immédiatement 10 crédits T-Kalé pour explorer nos services !
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </motion.div>

        {/* Right side - Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Card className="bg-white/10 backdrop-blur-md border-cyan-500/30 shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl text-white mb-2">
                Créer mon compte gratuit
              </CardTitle>
              <CardDescription className="text-cyan-200">
                Aucune carte bancaire requise
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-cyan-100">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                      errors.email ? 'border-red-500' : ''
                    }`}
                    placeholder="votre@email.com"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm">{errors.email}</p>
                  )}
                </div>

                {/* Username */}
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-cyan-100">Nom d'utilisateur</Label>
                  <Input
                    id="username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                      errors.username ? 'border-red-500' : ''
                    }`}
                    placeholder="Votre nom"
                    disabled={isSubmitting}
                  />
                  {errors.username && (
                    <p className="text-red-400 text-sm">{errors.username}</p>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-cyan-100">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                      errors.password ? 'border-red-500' : ''
                    }`}
                    placeholder="••••••••"
                    disabled={isSubmitting}
                  />
                  {errors.password && (
                    <p className="text-red-400 text-sm">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-cyan-100">
                    Confirmer le mot de passe
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                      errors.confirmPassword ? 'border-red-500' : ''
                    }`}
                    placeholder="••••••••"
                    disabled={isSubmitting}
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-400 text-sm">{errors.confirmPassword}</p>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-6 text-lg mt-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Créer mon compte gratuit
                    </>
                  )}
                </Button>

                {/* Alternatives */}
                <div className="text-center text-cyan-200 text-sm mt-4 space-y-2">
                  <p>
                    Vous avez un code d'accès ?{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/register-with-code')}
                      className="text-cyan-300 hover:text-cyan-100 underline font-semibold"
                    >
                      Utiliser un code
                    </button>
                  </p>
                  <p>
                    Déjà un compte ?{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="text-cyan-300 hover:text-cyan-100 underline font-semibold"
                    >
                      Se connecter
                    </button>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default FreeRegistrationPage;
